import { useEffect, useState } from 'react'
import { AppLaunchDto } from '@ivy-monorepo/shared'
import Table from '../../components/Table'
import Pagination from '../../components/Pagination'
import { adminService } from '../../services/admin.service'
import { useSearch } from '../../contexts/SearchContext'
import { useNavigate, useSearchParams } from 'react-router-dom'

function calculateOptimalPageSize(): number {
  const viewportHeight = window.innerHeight
  const rowHeight = 56
  const headerHeight = 172
  const paginationHeight = 60

  const availableHeight = viewportHeight - headerHeight - paginationHeight
  const calculatedPageSize = Math.floor(availableHeight / rowHeight)
  return Math.max(10, Math.min(100, calculatedPageSize))
}

const AppLaunchesPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { searchValue } = useSearch()

  const [appLaunches, setAppLaunches] = useState<AppLaunchDto[]>([])
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pageSize: 10,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })

  const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchValue)

  const currentPage = parseInt(searchParams.get('page') || '1')
  const urlPageSize = searchParams.get('pageSize')
  const currentPageSize = urlPageSize ? parseInt(urlPageSize) : calculateOptimalPageSize()
  const currentSortBy = searchParams.get('sortBy') || 'createdAt'
  const currentSortOrder = (searchParams.get('sortOrder') as 'asc' | 'desc') || 'desc'

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchValue])

  useEffect(() => {
    const fetchAppLaunches = async () => {
      try {
        const response = await adminService.getAppLaunches(
          currentPage,
          currentPageSize,
          debouncedSearchValue,
          currentSortBy,
          currentSortOrder
        )
        setAppLaunches(response.appLaunches)
        setPagination(response.pagination)
      } catch (err) {
        console.log('Failed to fetch app launches', err)
      }
    }

    fetchAppLaunches()
  }, [currentPage, currentPageSize, currentSortBy, currentSortOrder, debouncedSearchValue, searchParams])

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('page', page.toString())
    navigate(`?${newParams.toString()}`)
  }

  const handleSortChange = (sortBy: string, sortOrder: string) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('sortBy', sortBy)
    newParams.set('sortOrder', sortOrder)
    newParams.set('page', '1')
    navigate(`?${newParams.toString()}`)
  }

  return (
    <div className="users-container">
      <div className="users-header">
        <h2>App launches</h2>
        <Pagination currentPage={pagination.page} totalPages={pagination.totalPages} onPageChange={handlePageChange} />
      </div>

      <div className="users-table-container">
        <div className="table-wrapper">
          <Table
            id="app-launches"
            name="App launches"
            keyField="id"
            initialSortColumn={currentSortBy}
            initialSortOrder={currentSortOrder}
            columns={[
              { label: 'Created', accessor: 'createdAt', type: 'datetime' },
              { label: 'User', accessor: 'userFullName', type: 'text' as any },
              { label: 'Platform', accessor: 'platform', type: 'text' as any },
              { label: 'OS version', accessor: 'osVersion', type: 'text' as any },
              { label: 'Device model', accessor: 'deviceModel', type: 'text' as any },
              { label: 'App version', accessor: 'appVersion', type: 'text' as any },
              { label: 'Vendor ID', accessor: 'deviceVendorId', type: 'text' as any },
              { label: 'Location', accessor: 'latitude', type: 'coordinates' as any },
            ]}
            sourceData={appLaunches}
            isPropertyBarVisible={false}
            filterTerms=""
            onSortChange={handleSortChange}
            disableGlobalSearch={true}
            disableClientSort={true}
          />
        </div>
      </div>
    </div>
  )
}

export default AppLaunchesPage
